import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Upload, Download, AlertTriangle, CheckCircle2, Paperclip,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { api } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const money = (v) => Number(v || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

/** April-March. Before April the current year still belongs to the previous one. */
const currentFinancialYear = (now = new Date()) => {
  const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
};

/* ─────────────────────────────── Form 16 ─────────────────────────────── */

const Form16 = () => {
  const [employeeId, setEmployeeId] = useState('');
  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    if (!employeeId.trim()) { toast.error('An employee id is needed'); return; }
    setBusy('load');
    try {
      const { data } = await api.get(`/api/payroll/form16/${employeeId.trim()}`, {
        params: { financial_year: financialYear },
      });
      setForm(data);
    } catch (e) {
      setForm(null);
      toast.error(getErrorMessage(e, 'Could not build a Form 16'));
    } finally {
      setBusy('');
    }
  };

  const uploadPartA = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    setBusy('upload');
    try {
      await api.post(`/api/payroll/form16/${employeeId.trim()}/part-a`, body, {
        params: { financial_year: financialYear },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Part A attached');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not attach that file'));
    } finally {
      setBusy('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />Form 16
        </CardTitle>
        <p className="text-[13px] text-muted-foreground">
          Part B is computed from the payslips actually paid. Part A comes from
          TRACES after the quarterly return is filed and cannot be produced here —
          attach it once you have it, so an employee gets one document rather than
          half of one.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Employee ID</Label>
            <Input className="mt-1 w-40" value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Financial year</Label>
            <Input className="mt-1 w-32" value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)} placeholder="2026-27" />
          </div>
          <Button onClick={load} disabled={busy === 'load'}>
            {busy === 'load' ? 'Building…' : 'Build Part B'}
          </Button>
        </div>

        {form && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-border p-4 text-[13px]">
                <p className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  {form.employee_name || form.employee_id} · {form.financial_year}
                </p>
                {[
                  ['Months paid', form.months_paid],
                  ['Gross salary', money(form.gross_salary)],
                  ['Professional tax', money(form.professional_tax)],
                  ['PF (employee)', money(form.pf_employee)],
                  ['80C considered', money(form.section_80c_considered)],
                  ['TDS deducted', money(form.tds_deducted)],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="tabular-nums">{value}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-border p-4">
                <p className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  Part A
                </p>
                {form.part_a_available ? (
                  <div className="space-y-2 text-[13px]">
                    <Badge className="bg-emerald-500/15 text-emerald-700">
                      <CheckCircle2 className="mr-1 inline h-3 w-3" />attached
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      <a className="underline" href={form.part_a.download_url}
                        target="_blank" rel="noreferrer">
                        {form.part_a.file_name}
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] text-muted-foreground">
                    Not attached. Download it from TRACES after filing the quarter&apos;s
                    24Q, then upload it here.
                  </p>
                )}
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-[13px] hover:bg-accent">
                  <Upload className="h-3.5 w-3.5" />
                  {form.part_a_available ? 'Replace Part A' : 'Upload Part A'}
                  <input type="file" className="hidden" disabled={busy === 'upload'}
                    onChange={(e) => uploadPartA(e.target.files?.[0])} />
                </label>
              </div>
            </div>

            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(form.monthly || []).map((m) => (
                  <TableRow key={`${m.year}-${m.month}`}>
                    <TableCell>{m.month_name} {m.year}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(m.gross_earnings)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(m.tds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ──────────────────────────────── 24Q ──────────────────────────────── */

const Form24Q = () => {
  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [quarter, setQuarter] = useState('Q1');
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/api/payroll/form24q', {
        params: { financial_year: financialYear, quarter },
      });
      setSummary(data);
    } catch (e) {
      setSummary(null);
      toast.error(getErrorMessage(e, 'Could not compute the quarter'));
    } finally {
      setBusy(false);
    }
  };

  const downloadAnnexure = async () => {
    try {
      const { data } = await api.get('/api/payroll/form24q/annexure.csv', {
        params: { financial_year: financialYear, quarter },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `24Q-${financialYear}-${quarter}-annexure.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not export the annexure'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />Form 24Q
        </CardTitle>
        <p className="text-[13px] text-muted-foreground">
          The per-deductee figures a 24Q reports. This is not a filed return: the
          annexure exports as CSV for the return preparation utility, which is
          what validates and files it.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Financial year</Label>
            <Input className="mt-1 w-32" value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)} placeholder="2026-27" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Quarter</Label>
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              className="mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <Button onClick={load} disabled={busy}>{busy ? 'Computing…' : 'Compute'}</Button>
          {summary && (
            <Button variant="outline" onClick={downloadAnnexure}>
              <Download className="mr-2 h-4 w-4" />Annexure CSV
            </Button>
          )}
        </div>

        {summary && (
          <div className="space-y-3">
            {summary.missing_pan?.length > 0 && (
              <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <b>{summary.missing_pan.length} deductee(s) have no PAN on record.</b>{' '}
                  Filed that way the deduction goes at the higher section 206AA rate
                  and they cannot claim the credit — fix it before the return goes,
                  not after.
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              {[
                ['Deductees', summary.deductee_count],
                ['Amount paid', money(summary.total_gross)],
                ['TDS deducted', money(summary.total_tds)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-border p-3">
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
                </div>
              ))}
            </div>

            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Months</TableHead>
                  <TableHead className="text-right">Amount paid</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.deductees.map((d) => (
                  <TableRow key={d.employee_id}>
                    <TableCell className="font-medium">
                      {d.employee_id}
                      {summary.missing_pan?.includes(d.employee_id) && (
                        <Badge className="ml-2 bg-amber-500/15 text-amber-700">no PAN</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{d.months}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(d.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(d.tds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TaxFilings = ({ section = 'form16' }) => (
  <div className="space-y-4">
    {section === 'form16' ? <Form16 /> : <Form24Q />}
  </div>
);

export default TaxFilings;
